import { useGSAP } from "@hooks/useGSAP"
import gsap from "gsap"
import { forwardRef, MutableRefObject, ReactNode, useImperativeHandle, useRef } from "react"
import { Chip } from "./Chip"
import { cn } from "@utils/tw"

export type EyebrowType = HTMLDivElement & {
  tl: () => MutableRefObject<GSAPTimeline | null>
}

export const Eyebrow = forwardRef<
  EyebrowType,
  { href?: string | null; children: ReactNode; className?: string; chipClassName?: string; autoPlay?: boolean }
>(({ href, children, className, chipClassName, autoPlay = false }, outerRef) => {
  const ref = useRef<HTMLDivElement>(null)
  const tl = useRef<gsap.core.Timeline | null>(null)

  useImperativeHandle(
    outerRef,
    () =>
      Object.assign(ref.current!, {
        tl: () => tl,
      }),
    [ref],
  )

  useGSAP(
    () => {
      tl.current?.revert().kill()

      tl.current = gsap.timeline({ paused: !autoPlay })
      tl.current
        .from(".eyebrow-chip", { y: -50, opacity: 0, duration: 0.5, ease: "power3.out" }, 0)
        .from(".eyebrow-line-outer", { opacity: 0, duration: 0.5, ease: "power3.out" }, 0.25)
        .from(".eyebrow-line", { y: "-100%", duration: 0.5, ease: "power3.out" }, 0.25)

      if (autoPlay) {
        tl.current?.restart()
      }
    },
    { scope: ref },
  )

  return (
    <div ref={ref} className={cn("eyebrow relative w-fit", className)}>
      <Chip href={href} className={cn("eyebrow-chip", chipClassName)}>
        {children}
      </Chip>
      <div className="eyebrow-line-outer h-11 w-[1px] absolute top-full left-1/2 overflow-hidden">
        <div className="eyebrow-line h-6 w-[1px] border-l border-dashed border-current" />
      </div>
    </div>
  )
})
